import { FormEvent, useMemo, useState } from 'react';
import Modal from '../../../../shared/modal';
import {
    AGENT_CLIENTS,
    ICreateAgentClientRequest,
} from '../../../../store/users/usersApi';

type CreateAgentClientForm = ICreateAgentClientRequest;

const initialForm: CreateAgentClientForm = {
    agents: ['rebecca'],
    name: '',
    email: '',
    username: '',
    password: '',
    country: 'GB',
    companyName: '',
};

const toAgentLabel = (agent: string) =>
    agent ? `${agent.charAt(0).toUpperCase()}${agent.slice(1)}` : '';

const validatePassword = (password: string) =>
    password.length >= 6
    && /[A-Z]/.test(password)
    && /[^A-Za-z0-9]/.test(password);

const inputClassName = 'w-full rounded-xl border bg-white dark:bg-black px-3 py-2 text-sm text-black dark:text-white border-gray-200 dark:border-borderColor-darkMode focus:outline-none focus:border-gray-300 dark:focus:border-gray-600';

const CreateAgentClientModal = ({
    open,
    loading,
    error,
    onClose,
    onSubmit,
}: {
    open: boolean;
    loading?: boolean;
    error?: string | null;
    onClose: () => void;
    onSubmit: (payload: ICreateAgentClientRequest) => Promise<boolean>;
}) => {
    const [form, setForm] = useState<CreateAgentClientForm>(initialForm);
    const [touchedSubmit, setTouchedSubmit] = useState(false);

    const updateField = <K extends keyof CreateAgentClientForm>(
        field: K,
        value: CreateAgentClientForm[K],
    ) => {
        setForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const validationErrors = useMemo(() => {
        const errors: Partial<Record<keyof CreateAgentClientForm, string>> = {};
        if (form.agents.length === 0) errors.agents = 'Select at least one agent.';
        if (!form.name.trim()) errors.name = 'Name is required.';
        if (!form.email.trim()) errors.email = 'Email is required.';
        if (!form.username.trim()) errors.username = 'Username is required.';
        if (!form.country.trim()) errors.country = 'Country is required.';
        if (!validatePassword(form.password)) {
            errors.password = 'Password must be at least 6 characters and include one uppercase letter and one symbol.';
        }
        if (form.agents.includes('leo') && !form.companyName?.trim()) {
            errors.companyName = 'Company name is required for Leo.';
        }
        return errors;
    }, [form]);

    const hasValidationErrors = Object.keys(validationErrors).length > 0;

    const resetAndClose = () => {
        if (loading) return;
        setForm(initialForm);
        setTouchedSubmit(false);
        onClose();
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setTouchedSubmit(true);
        if (hasValidationErrors || loading) return;

        const payload: ICreateAgentClientRequest = {
            agents: [...form.agents],
            name: form.name.trim(),
            email: form.email.trim(),
            username: form.username.trim(),
            password: form.password,
            country: form.country.trim().toUpperCase(),
            ...(form.companyName?.trim() ? { companyName: form.companyName.trim() } : {}),
        };

        const created = await onSubmit(payload);
        if (!created) return;
        setForm(initialForm);
        setTouchedSubmit(false);
    };

    const showError = (key: keyof CreateAgentClientForm) =>
        touchedSubmit && validationErrors[key];

    const toggleAgent = (agent: CreateAgentClientForm['agents'][number]) => {
        updateField(
            'agents',
            form.agents.includes(agent)
                ? form.agents.filter((selectedAgent) => selectedAgent !== agent)
                : [...form.agents, agent],
        );
    };

    return (
        <Modal
            open={open}
            onClose={resetAndClose}
            title='Create New Client'
            contentContainerStyle='w-full max-w-[620px] !max-h-[90vh] overflow-y-auto'
        >
            <form className='pt-2 pb-1' onSubmit={handleSubmit}>
                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                    <div className='md:col-span-2'>
                        <div className='mb-2 flex items-center justify-between gap-3'>
                            <label className='block text-xs font-medium text-gray-600 dark:text-gray-300'>
                                Agents
                            </label>
                            <span className='text-[11px] text-gray-400 dark:text-gray-500'>
                                Select one or more
                            </span>
                        </div>
                        <div className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
                            {AGENT_CLIENTS.map((agent) => (
                                <button
                                    key={agent}
                                    type='button'
                                    aria-pressed={form.agents.includes(agent)}
                                    onClick={() => toggleAgent(agent)}
                                    className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2 text-left text-sm font-medium transition-colors ${
                                        form.agents.includes(agent)
                                            ? 'border-[#00330f] bg-emerald-50 text-[#00330f] ring-1 ring-[#00330f] dark:border-emerald-400 dark:bg-emerald-900/30 dark:text-emerald-200 dark:ring-emerald-400'
                                            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50 dark:border-borderColor-darkMode dark:bg-black dark:text-gray-300 dark:hover:border-gray-600'
                                    }`}
                                >
                                    <span>{toAgentLabel(agent)}</span>
                                    <span
                                        aria-hidden='true'
                                        className={`flex h-4 w-4 items-center justify-center rounded-full border text-[10px] ${
                                            form.agents.includes(agent)
                                                ? 'border-[#00330f] bg-[#00330f] text-white dark:border-emerald-400 dark:bg-emerald-400 dark:text-black'
                                                : 'border-gray-300 text-transparent dark:border-gray-600'
                                        }`}
                                    >
                                        &#10003;
                                    </span>
                                </button>
                            ))}
                        </div>
                        {showError('agents') && (
                            <p className='mt-1 text-xs text-red-600 dark:text-red-400'>{validationErrors.agents}</p>
                        )}
                    </div>

                    <div>
                        <label className='mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300'>
                            Country
                        </label>
                        <input
                            value={form.country}
                            onChange={(event) => updateField('country', event.target.value)}
                            placeholder='GB'
                            maxLength={2}
                            className={inputClassName}
                        />
                        {showError('country') && (
                            <p className='mt-1 text-xs text-red-600 dark:text-red-400'>{validationErrors.country}</p>
                        )}
                    </div>

                    <div>
                        <label className='mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300'>
                            Name
                        </label>
                        <input
                            value={form.name}
                            onChange={(event) => updateField('name', event.target.value)}
                            placeholder='Client Name'
                            className={inputClassName}
                        />
                        {showError('name') && (
                            <p className='mt-1 text-xs text-red-600 dark:text-red-400'>{validationErrors.name}</p>
                        )}
                    </div>

                    <div>
                        <label className='mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300'>
                            Company Name
                        </label>
                        <input
                            value={form.companyName || ''}
                            onChange={(event) => updateField('companyName', event.target.value)}
                            placeholder={form.agents.includes('leo') ? 'Required for Leo' : 'Optional'}
                            className={inputClassName}
                        />
                        {showError('companyName') && (
                            <p className='mt-1 text-xs text-red-600 dark:text-red-400'>{validationErrors.companyName}</p>
                        )}
                    </div>

                    <div>
                        <label className='mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300'>
                            Email
                        </label>
                        <input
                            value={form.email}
                            onChange={(event) => updateField('email', event.target.value)}
                            placeholder='client@example.com'
                            type='email'
                            className={inputClassName}
                        />
                        {showError('email') && (
                            <p className='mt-1 text-xs text-red-600 dark:text-red-400'>{validationErrors.email}</p>
                        )}
                    </div>

                    <div>
                        <label className='mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300'>
                            Username
                        </label>
                        <input
                            value={form.username}
                            onChange={(event) => updateField('username', event.target.value)}
                            placeholder='clientusername'
                            className={inputClassName}
                        />
                        {showError('username') && (
                            <p className='mt-1 text-xs text-red-600 dark:text-red-400'>{validationErrors.username}</p>
                        )}
                    </div>

                    <div className='md:col-span-2'>
                        <label className='mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300'>
                            Password
                        </label>
                        <input
                            value={form.password}
                            onChange={(event) => updateField('password', event.target.value)}
                            placeholder='StrongPass1!'
                            type='password'
                            className={inputClassName}
                        />
                        <p className={`mt-1 text-xs ${
                            showError('password')
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-gray-500 dark:text-gray-400'
                        }`}>
                            At least 6 characters, one uppercase letter and one symbol.
                        </p>
                    </div>
                </div>

                {error && (
                    <p className='mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-300'>
                        {error}
                    </p>
                )}

                <div className='mt-6 flex justify-end gap-3 sticky bottom-0 bg-white dark:bg-bgColor-darkMode py-3'>
                    <button
                        type='button'
                        onClick={resetAndClose}
                        disabled={loading}
                        className='rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 dark:border-borderColor-darkMode dark:text-primary-darkMode hover:bg-gray-50 dark:hover:bg-bgColor-darkMode disabled:opacity-50'
                    >
                        Cancel
                    </button>
                    <button
                        type='submit'
                        disabled={loading}
                        className='rounded-xl border border-[#00330f] bg-[#00330f] px-4 py-2 text-sm font-semibold text-white dark:border-white dark:bg-white dark:text-black hover:opacity-90 disabled:opacity-50'
                    >
                        {loading ? 'Creating...' : 'Create Client'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default CreateAgentClientModal;
